import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input } from 'reactstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { getClosestFutureDate } from 'Common.functions';
import {
  findInterviewDateInCollection,
  getInterviewDatePk,
  isPrescreenInterviewDate,
  normalizeInterviewDate,
  normalizeInterviewDatePk,
  PRESCREEN_INTERVIEW_DATE,
  serializeInterviewDateForRoute,
} from 'Services/interviewDate';
import { isEvaluatorDateSelectorCompact } from '../evaluatorHeaderLayout';

export const getInterviewDateDisplayLabel = (item) => {
  if (!item) {
    return '';
  }

  const date = dayjs(item.DateOfInterview || item.InterviewDatesVLDate).format('ddd, MMM D, YYYY');
  const label = item.Label ? ` ${item.Label}` : '';
  return `${date}${label} (${item.CountOfCandidates || 0} Evals)`;
};

const DateDisplay = ({ date = {}, setDate, dateArray = [], season }) => {
  const [chosenDate, setChosenDate] = useState('');
  const [isCompact, setIsCompact] = useState(false);
  const selectContainerRef = useRef(null);
  const history = useHistory();
  const location = useLocation();
  const allowPrescreen = !!(season && season.AllowPrescreen);
  const routeInterviewDate = useMemo(() => {
    const match = location.pathname.match(/^\/user\/evaluate\/([^/?#]+)/i);
    return match && match[1] ? decodeURIComponent(match[1]) : null;
  }, [location.pathname]);

  const replaceEvaluationRoute = useCallback(
    (interviewDate) => {
      const nextPk = serializeInterviewDateForRoute(interviewDate);
      const nextPath = `/user/evaluate/${nextPk}`;

      if (nextPk && history.location.pathname !== nextPath) {
        history.replace(nextPath);
      }
    },
    [history],
  );

  const fallbackDate = useMemo(() => {
    if (allowPrescreen) {
      return PRESCREEN_INTERVIEW_DATE;
    }

    if (!Array.isArray(dateArray) || dateArray.length === 0) {
      return null;
    }

    return normalizeInterviewDate(getClosestFutureDate(dateArray), dateArray);
  }, [allowPrescreen, dateArray]);

  useEffect(() => {
    if (!season) {
      return;
    }

    const routePk = normalizeInterviewDatePk(routeInterviewDate);
    const routeDate = isPrescreenInterviewDate(routePk)
      ? (allowPrescreen ? PRESCREEN_INTERVIEW_DATE : null)
      : findInterviewDateInCollection(routePk, dateArray);
    const propDate = normalizeInterviewDate(date, dateArray);
    const hasExplicitRoute = routePk != null;
    const hasUnresolvedStandardRoute = hasExplicitRoute && !isPrescreenInterviewDate(routePk) && !routeDate;

    if (location.pathname.toUpperCase().includes('/USER/EVALUATE')) {
      console.log('[EvaluatorPortal Debug] DateDisplay sync', {
        pathname: location.pathname,
        routeInterviewDate,
        routePk,
        routeDatePk: getInterviewDatePk(routeDate),
        propDatePk: getInterviewDatePk(propDate),
        fallbackDatePk: getInterviewDatePk(fallbackDate),
        dateArrayLength: Array.isArray(dateArray) ? dateArray.length : 0,
        hasExplicitRoute,
        hasUnresolvedStandardRoute,
      });
    }

    if (hasExplicitRoute) {
      if (routeDate) {
        setChosenDate(getInterviewDatePk(routeDate));
        return;
      }

      // For non-prescreen dates, wait until the available date list loads before correcting the URL.
      if (hasUnresolvedStandardRoute && !propDate) {
        setChosenDate(getInterviewDatePk(routePk));
        return;
      }
    }

    if (propDate) {
      const propPk = getInterviewDatePk(propDate);
      setChosenDate(propPk);

      if (!hasExplicitRoute || routePk !== normalizeInterviewDatePk(propPk)) {
        replaceEvaluationRoute(propDate);
      }
      return;
    }

    if (fallbackDate) {
      if (hasExplicitRoute) {
        setChosenDate(getInterviewDatePk(routePk));
        return;
      }

      const fallbackPk = getInterviewDatePk(fallbackDate);
      setChosenDate(fallbackPk);
      replaceEvaluationRoute(fallbackDate);
      return;
    }

    setChosenDate('');
  }, [routeInterviewDate, date, dateArray, season, fallbackDate, replaceEvaluationRoute, location.pathname, allowPrescreen]);

  useEffect(() => {
    const updateSelectorSize = () => {
      const container = selectContainerRef.current;
      const containerWidth = container ? container.getBoundingClientRect().width : 0;
      const nextIsCompact = isEvaluatorDateSelectorCompact(containerWidth);
      setIsCompact((currentIsCompact) => (currentIsCompact === nextIsCompact ? currentIsCompact : nextIsCompact));
    };

    updateSelectorSize();
    window.addEventListener('resize', updateSelectorSize);

    const ResizeObserverConstructor = window.ResizeObserver;
    const resizeObserver = ResizeObserverConstructor && selectContainerRef.current
      ? new ResizeObserverConstructor(updateSelectorSize)
      : null;
    if (resizeObserver) {
      resizeObserver.observe(selectContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateSelectorSize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [allowPrescreen, dateArray.length, season]);

  const handleChange = (e) => {
    history.push(`/user/evaluate/${e.target.value}`);
  };

  if (!season) {
    return <div />;
  }

  if (dateArray.length === 1 && !allowPrescreen) {
    return (
      <div
        style={{
          color: '#fff',
          display: 'flex',
          textAlign: 'center',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {dayjs(dateArray[0].DateOfInterview || dateArray[0].InterviewDatesVLDate).format('ddd, MMM D, YYYY')}
      </div>
    );
  }

  if (dateArray.length === 0 && allowPrescreen) {
    return (
      <div
        style={{
          color: '#fff',
          display: 'flex',
          textAlign: 'left',
          justifyContent: 'left',
          alignItems: 'center',
          flexDirection: 'row',
          fontSize: 16,
          width: '100%',
          padding: 10,
        }}
      >
        Prescreen
      </div>
    );
  }

  const sortedDates = [...dateArray].sort((a, b) => {
    const dateA = dayjs(`${a.DateOfInterview} ${a.StartTime}`, 'YYYY-MM-DD HH:mm:ss').valueOf();
    const dateB = dayjs(`${b.DateOfInterview} ${b.StartTime}`, 'YYYY-MM-DD HH:mm:ss').valueOf();

    if (dateA !== dateB) {
      return dateA - dateB;
    }

    return a.pk_InterviewDate - b.pk_InterviewDate;
  });
  const selectedDateLabel = sortedDates.find((item) => `${item.pk_InterviewDate}` === `${chosenDate}`);
  const accessibleLabel = allowPrescreen && chosenDate === 'Prescreen'
    ? 'Selected interview session: Prescreen'
    : selectedDateLabel
      ? `Selected interview session: ${getInterviewDateDisplayLabel(selectedDateLabel)}`
      : 'Select an interview session';

  return (
    <div ref={selectContainerRef} style={{ minWidth: 0, width: '100%' }}>
      <Input
        type="select"
        name="select"
        className="options"
        value={chosenDate}
        onChange={handleChange}
        style={{ width: '100%', fontSize: isCompact ? 14 : undefined }}
        aria-label={accessibleLabel}
      >
        {allowPrescreen && <option value="Prescreen">Prescreen</option>}
        {sortedDates.map((item) => {
          return (
            <option key={item.pk_InterviewDate} value={item.pk_InterviewDate}>
              {getInterviewDateDisplayLabel(item)}
            </option>
          );
        })}
      </Input>
    </div>
  );
};

export default DateDisplay;
